'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import logger from '@/lib/logger';
import { useAnalytics } from '@/hooks/useAnalytics';
import SpotifyCurrently from '@/components/SpotifyCurrently';
import BookCurrently from '@/components/BookCurrently';
// Type-only import — erased at build, so the server-only data lib never enters
// the client bundle.
import type { CurrentlyData } from '@/lib/currently';

// Module-level cache to prevent duplicate fetches on remount
let cachedResult: {
  data: CurrentlyData;
  timestamp: number;
} | null = null;
const CACHE_TTL = 60_000; // 1 minute, matches server-side cache

type CurrentlyFetchError = Error & {
  status?: number;
};

// Hoisted to module scope so the `throw` lives outside the component body — the
// React Compiler bails on a ThrowStatement inside try/catch and would skip
// memoizing Currently. Tags the error with the HTTP status so the caller can
// report it.
async function fetchCurrentlyData(): Promise<CurrentlyData> {
  const response = await fetch('/api/currently');
  if (!response.ok) {
    const error: CurrentlyFetchError = new Error('Failed to fetch currently data');
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// Skeleton loader component
const SkeletonItem = t0 => {
  const $ = _c(5);
  const {
    type
  } = t0;
  const t1 = type === "music" ? "Last played" : "Reading";
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="size-[280px] animate-pulse rounded-[56px] bg-zinc-200 dark:bg-zinc-700" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = <div className="flex flex-col items-start gap-2">{t2}{t3}</div>;
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
export default function Currently({
  vertical = true,
  initialData = null
}: {
  vertical?: boolean;
  initialData?: CurrentlyData | null;
}) {
  // Seed from server-rendered data so there's no loading skeleton or client
  // fetch waterfall on first paint.
  const [isLoading, setIsLoading] = useState(!initialData);
  const [data, setData] = useState<CurrentlyData | null>(initialData);
  const [error] = useState<string | null>(null);
  const {
    trackError
  } = useAnalytics();
  const loadCurrently = async () => {
    // Use cached data if still fresh (prevents duplicate fetches on remount)
    if (cachedResult && Date.now() - cachedResult.timestamp < CACHE_TTL) {
      setData(cachedResult.data);
      setIsLoading(false);
      return;
    }
    try {
      const currentlyData = await fetchCurrentlyData();
      cachedResult = {
        data: currentlyData,
        timestamp: Date.now()
      };
      setData(currentlyData);
    } catch (err) {
      logger.error({
        error: err
      }, 'Error fetching currently data');
      if (err instanceof Error && err.message === 'Failed to fetch currently data') {
        // HTTP error from our API — report it with the status code.
        trackError(err, {
          endpoint: '/api/currently',
          status_code: (err as CurrentlyFetchError).status
        });
      } else if (err instanceof Error && !err.message.includes('Failed to fetch')) {
        // A real failure (JSON parse, etc.). Browser network blips surface as
        // 'Failed to fetch' and are expected, so they stay unreported.
        trackError(err, {
          endpoint: '/api/currently'
        });
      }
    }
    // No `finally`: the React Compiler bails on try/finally. The catch above
    // swallows and nothing returns early, so this always runs.
    setIsLoading(false);
  };
  useEffect(() => {
    // Prime the module cache with server-rendered data so the mount refresh is
    // a no-op while it's still fresh (no redundant network request).
    if (initialData && !cachedResult) {
      cachedResult = {
        data: initialData,
        timestamp: Date.now()
      };
    }
    void loadCurrently();
    // Mount-only: prime the cache + refresh once. The module-level cache dedupes
    // across remounts, so loadCurrently/initialData are intentionally not deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If there's an error, show a minimal error state
  if (error) {
    return <div className="mt-6 space-y-4">
        <h2 className="font-jakarta text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Currently
        </h2>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{error}</p>
      </div>;
  }
  return <div className="mt-6 space-y-4">
      <h2 className="font-jakarta text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        Currently
      </h2>

      {isLoading ? <div className={vertical ? 'flex flex-col gap-4' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
          <SkeletonItem type="music" />
          <SkeletonItem type="book" />
        </div> : <div className={vertical ? 'flex flex-col gap-4' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
          {data?.spotify && <SpotifyCurrently data={data.spotify} />}
          {data?.literal && <BookCurrently data={data.literal} />}
        </div>}
    </div>;
}